import { useEffect, useState } from "react";
import { Users, CreditCard, TrendingUp, AlertTriangle, Building2, Wallet, UserPlus, QrCode } from "lucide-react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

function Metric({ icon: Icon, label, value, sub, warn }) {
  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${warn ? "border-brand-orange/40 bg-brand-orange/5" : "border-border/60 bg-card"}`}>
      <div className="flex items-center justify-between">
        <p className="overline-label text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${warn ? "text-brand-orange" : "text-brand-greenLight"}`} />
      </div>
      <p className="mt-3 font-heading text-3xl font-extrabold">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function AdminOverview() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <div className="grid gap-4 md:grid-cols-3"><Skeleton className="h-32 rounded-2xl" /><Skeleton className="h-32 rounded-2xl" /><Skeleton className="h-32 rounded-2xl" /></div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-overview">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Overview</h1>
        <p className="mt-1 text-muted-foreground">Live snapshot of the TiplyMe network.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric icon={TrendingUp} label="Tip volume" value={`R${d.total_volume.toFixed(0)}`} sub={`${d.tip_count} tips processed`} />
        <Metric icon={Users} label="Workers" value={d.workers} sub="Active gig workers" />
        <Metric icon={UserPlus} label="Pending approvals" value={d.pending_approvals || 0} sub="Self sign-ups to review" warn={(d.pending_approvals || 0) > 0} />
        <Metric icon={QrCode} label="Needs lanyard" value={d.flagged_for_qr || 0} sub="Send QR/NFC cards" warn={(d.flagged_for_qr || 0) > 0} />
        <Metric icon={CreditCard} label="Cards" value={`${d.cards_linked}/${d.cards_total}`} sub={`${d.cards_unlinked} unlinked`} />
        <Metric icon={Building2} label="Corporates" value={d.workplaces} sub="Partner workplaces" />
        <Metric icon={Wallet} label="Pending cashouts" value={d.pending_cashouts} sub="Awaiting processing" />
        <Metric icon={AlertTriangle} label="Overdue >24h" value={d.overdue_cashouts} sub="Needs attention" warn={d.overdue_cashouts > 0} />
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
        <h2 className="font-heading text-lg font-bold">Recent transactions</h2>
        <div className="mt-4 divide-y divide-border/60">
          {d.recent_transactions.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No transactions yet.</p>}
          {d.recent_transactions.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-3" data-testid="recent-txn">
              <div>
                <p className="text-sm font-semibold">{t.worker_name}</p>
                <p className="text-xs text-muted-foreground">{t.processor} · {new Date(t.created_at).toLocaleString("en-ZA")}</p>
              </div>
              <span className="font-heading font-bold text-brand-greenLight">+R{t.amount.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
